'use client';

import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import {
  UPZI_LOGIN_SUCCESS_EVENT_NAME,
  UPZI_LOGOUT_EVENT_NAME,
  UPZI_SESSION_EXPIRED_EVENT_NAME,
} from '@/common';
import { DEFAULT_JOB_FILTERS, getJobSearchPath } from '@/services/jobs';
import { useJobSearchSuggestions } from '@/services/jobs/use-job-search-suggestions';
import { JobSearchHero } from './job-search-hero';
import {
  getSearchTrackingProperties,
  useOpportunityTracking,
} from '@/hooks/use-opportunity-tracking';
import { OPPORTUNITY_TRACKING_EVENTS } from '@/services/opportunity-tracking';
import {
  rememberJobSearchMethod,
  rememberJobSearchSourceScreen,
  rememberJobSearchStartedAt,
  SOURCE_SCREENS,
} from '@/services/opportunity-tracking/source-screen';

type JobDetailSearchHeroProps = {
  isLoggedIn: boolean;
};

export function JobDetailSearchHero({ isLoggedIn }: JobDetailSearchHeroProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { trackOpportunityEvent } = useOpportunityTracking();
  const [query, setQuery] = useState('');
  const [isSuggestionsActivated, setIsSuggestionsActivated] = useState(false);
  const [clientAuthOverride, setClientAuthOverride] = useState<boolean | null>(
    null
  );
  const [authSessionVersion, setAuthSessionVersion] = useState(0);
  const isAuthenticated = clientAuthOverride ?? isLoggedIn;
  const { isLoading: isSuggestionLoading, keywords: suggestionKeywords } =
    useJobSearchSuggestions(
      query,
      isAuthenticated && isSuggestionsActivated,
      authSessionVersion
    );

  useEffect(() => {
    const handleLoginSuccess = () => {
      setClientAuthOverride(true);
      setAuthSessionVersion(version => version + 1);
    };
    const handleLogout = () => {
      setClientAuthOverride(false);
      setAuthSessionVersion(version => version + 1);
    };

    window.addEventListener(UPZI_LOGIN_SUCCESS_EVENT_NAME, handleLoginSuccess);
    window.addEventListener(UPZI_LOGOUT_EVENT_NAME, handleLogout);
    window.addEventListener(UPZI_SESSION_EXPIRED_EVENT_NAME, handleLogout);

    return () => {
      window.removeEventListener(
        UPZI_LOGIN_SUCCESS_EVENT_NAME,
        handleLoginSuccess
      );
      window.removeEventListener(UPZI_LOGOUT_EVENT_NAME, handleLogout);
      window.removeEventListener(UPZI_SESSION_EXPIRED_EVENT_NAME, handleLogout);
    };
  }, []);

  const navigateToJobs = (submittedQuery: string) => {
    rememberJobSearchStartedAt();
    rememberJobSearchSourceScreen(pathname);
    rememberJobSearchMethod('type');
    void trackOpportunityEvent({
      eventName: OPPORTUNITY_TRACKING_EVENTS.search,
      properties: {
        ...getSearchTrackingProperties(submittedQuery, 'type'),
        source_screen: SOURCE_SCREENS.JOB_DETAILS,
        source_section: 'hero_section',
      },
    });
    router.push(getJobSearchPath(submittedQuery, '', DEFAULT_JOB_FILTERS));
  };

  return (
    <JobSearchHero
      isLoading={false}
      isSuggestionsEnabled={isSuggestionsActivated}
      isSuggestionLoading={isSuggestionLoading}
      onQueryChange={setQuery}
      onSuggestionsActivate={() => setIsSuggestionsActivated(true)}
      onSubmit={navigateToJobs}
      query={query}
      suggestionKeywords={suggestionKeywords}
    />
  );
}
